"use client";

import { useEffect, useState } from "react";
import Icon from "@/components/ui/AppIcon";
import { apiFetch } from "@/lib/api/client";
import type { DashboardSummary } from "../types";

type Props = {
  data: DashboardSummary['kpis'] | null;
  loading: boolean;
};

export default function KPIBentoGrid({ data, loading }: Props) {

  if (loading || !data) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className={`animate-pulse bg-white rounded-xl border border-border h-24 ${i === 0 || i === 5 ? "col-span-2" : ""}`} />
        ))}
      </div>
    );
  }

  const cards = [
    {
      id: "active",
      label: "Active Cases",
      value: data.activeCases,
      sub: "across all IP types",
      icon: "DocumentTextIcon",
      tone: "bg-white border-border",
      iconTone: "bg-primary/10 text-primary",
      span: "col-span-2",
      breakdown: [
        { label: "Patent", value: data.casesByIpType.patent, color: "bg-blue-500" },
        { label: "Trademark", value: data.casesByIpType.trademark, color: "bg-violet-500" },
        { label: "Design", value: data.casesByIpType.design, color: "bg-amber-500" },
      ],
    },
    {
      id: "today",
      label: "Tasks Due Today",
      value: data.tasksDueToday,
      sub: "open tasks",
      icon: "CalendarDaysIcon",
      tone: "bg-amber-50 border-amber-200",
      iconTone: "bg-amber-100 text-amber-600",
    },
    {
      id: "overdue",
      label: "Overdue Tasks",
      value: data.overdueTasks,
      sub: "require immediate action",
      icon: "ExclamationTriangleIcon",
      tone: "bg-red-50 border-red-200",
      iconTone: "bg-red-100 text-red-600",
    },
    {
      id: "renewals",
      label: "Renewals in 90 Days",
      value: data.renewalsIn90Days,
      sub: `${data.renewalsBucket.within30} within 30 days`,
      icon: "ArrowPathIcon",
      tone: "bg-amber-50 border-amber-200",
      iconTone: "bg-amber-100 text-amber-600",
    },
    {
      id: "outstanding",
      label: "Outstanding Invoices",
      value: data.outstandingInvoicesCount,
      sub: data.outstandingInvoicesCount === 1 ? "invoice" : "invoices",
      icon: "BanknotesIcon",
      tone: "bg-blue-50 border-blue-100",
      iconTone: "bg-blue-100 text-blue-600",
    },
    {
      id: "filed",
      label: "Filed This Month",
      value: data.filedThisMonth,
      sub: "across all IP types",
      icon: "ArrowUpTrayIcon",
      tone: "bg-green-50 border-green-100",
      iconTone: "bg-green-100 text-green-600",
      span: "col-span-2 md:col-span-2",
      breakdown: [
        { label: "Patent", value: data.filedThisMonthByIpType.patent, color: "bg-blue-500" },
        { label: "Trademark", value: data.filedThisMonthByIpType.trademark, color: "bg-violet-500" },
        { label: "Design", value: data.filedThisMonthByIpType.design, color: "bg-amber-500" },
      ],
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
      {cards.map((card) => (
        <div key={card.id} className={`rounded-xl border p-3 flex flex-col gap-2 ${card.tone} ${card.span ?? ""}`}>
          <div className="flex items-start justify-between">
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${card.iconTone}`}>
              <Icon name={card.icon as any} size={14} variant="outline" />
            </div>
          </div>

          <div className="flex items-end justify-between gap-4 mt-auto">
            <div>
              <p className="text-[11px] font-500 text-muted-foreground tracking-wide uppercase">{card.label}</p>
              <p className="tabular-nums font-700 leading-none mt-0.5 text-2xl text-foreground">{card.value}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">{card.sub}</p>
            </div>

            {card.breakdown && (
              <div className="flex-1 max-w-[60%] space-y-1 mb-0.5">
                <div className="flex h-1.5 rounded-full overflow-hidden gap-0.5 bg-muted/20">
                  {card.breakdown.map((seg) => {
                    const total = card.breakdown!.reduce((a, b) => a + b.value, 0) || 1;
                    return <div key={seg.label} className={`${seg.color} h-full`} style={{ width: `${(seg.value / total) * 100}%` }} />;
                  })}
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  {card.breakdown.map((seg) => (
                    <span key={seg.label} className="flex items-center gap-1 text-[10px] text-muted-foreground">
                      <span className={`w-1.5 h-1.5 rounded-full ${seg.color}`} />
                      <span className="font-500">{seg.value}</span> {seg.label}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
